import { StarIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { teamGoals } from "@/lib/teamGoals";
import { cn } from "@/lib/utils";

const RADIUS = 15.9155;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const STACK_THRESHOLD = 6;
const COMPACT_MAX_SIZE_PX = 80;
const MINI_MAX_SIZE_PX = 64;

export const ProgressRing = ({
  label,
  value,
  goal,
  displayValue,
  displayGoal,
  colorClass,
  hasGoal = true,
  isLabelBottomAligned = false,
  labelClassName,
  sizePx = 88,
  layout = "column",
}: {
  label: string;
  value: number;
  goal: number;
  displayValue?: string;
  displayGoal?: string;
  colorClass: string;
  hasGoal?: boolean; // Non-staffers don't get goals, stars, etc
  isLabelBottomAligned?: boolean;
  labelClassName?: string;
  sizePx?: number;
  // column: value/goal inside the ring, label below. row: ring left, stats right.
  layout?: "column" | "row";
}) => {
  /* Display Values */
  const valueText = displayValue ?? value.toLocaleString();
  const goalText = displayGoal ?? Math.ceil(goal).toLocaleString();
  // Non-staffers don't have goals, so any progress > 0 fills the ring.
  const pct = hasGoal
    ? goal > 0
      ? Math.min(100, Math.round((value / goal) * 100))
      : 0
    : value > 0
      ? 100
      : 0;
  const dashLength = (pct / 100) * CIRCUMFERENCE;
  // Stack value over goal when the inline "value/goal" would be too wide — by
  // total length, or whenever the numerator alone hits 3+ digits.
  const shouldStack =
    hasGoal &&
    (valueText.length + goalText.length + 1 > STACK_THRESHOLD ||
      valueText.length >= 3);
  const stars = hasGoal ? teamGoals.starCount(value, goal) : 0;

  // Smaller rings (e.g. the dashboard goal cards) need tighter type + stroke
  // so the numbers don't overflow the ring; mini is one step tighter still.
  const isCompact = sizePx < COMPACT_MAX_SIZE_PX;
  const isMini = sizePx < MINI_MAX_SIZE_PX;
  const ringStrokeWidth = isCompact ? 2 : 3;
  // Long values (e.g. "$526.7K") overflow the compact ring, so scale the value
  // font down by string length while keeping short numbers large.
  const compactValueSize =
    valueText.length >= 7
      ? "text-[11px]"
      : valueText.length >= 5
        ? "text-[13px]"
        : "text-[15px]";
  const miniValueSize =
    valueText.length >= 7
      ? "text-[8px]"
      : valueText.length >= 5
        ? "text-[10px]"
        : "text-[11px]";
  const valueSize = isMini ? miniValueSize : compactValueSize;
  const valueTextClass = isCompact
    ? cn(valueSize, "font-medium tabular-nums leading-none text-slate-600")
    : "text-base font-medium tabular-nums leading-none text-slate-600";
  const goalTextClass = isMini
    ? "text-[8px] font-medium tabular-nums leading-tight text-slate-400"
    : isCompact
      ? "text-[9px] font-medium tabular-nums leading-tight text-slate-400"
      : "text-xs font-medium tabular-nums leading-tight text-slate-400";
  const inlineGoalTextClass = isMini
    ? "text-[8px] font-medium tabular-nums leading-none text-slate-400"
    : isCompact
      ? "text-[9px] font-medium tabular-nums leading-none text-slate-400"
      : "text-xs font-medium tabular-nums leading-none text-slate-400";
  const labelTextClass = isMini
    ? "text-[9px] font-medium leading-tight text-slate-500"
    : isCompact
      ? "text-[11px] font-medium leading-tight text-slate-500"
      : "text-xs font-medium leading-tight text-slate-500";

  /* Render */
  const renderCenterText = () => {
    if (!hasGoal) {
      return <Text className={valueTextClass}>{valueText}</Text>;
    }
    if (shouldStack) {
      return (
        <Column className="items-center leading-none">
          <Text className={valueTextClass}>{valueText}</Text>
          <Text className={goalTextClass}>/{goalText}</Text>
        </Column>
      );
    }
    return (
      <Row className="items-baseline gap-0.5">
        <Text className={valueTextClass}>{valueText}</Text>
        <Text className={inlineGoalTextClass}>/{goalText}</Text>
      </Row>
    );
  };

  const starIcons = Array.from({ length: stars }).map((_, i) => (
    <StarIcon
      key={i}
      className={cn(
        isMini ? "h-2.5 w-2.5" : "h-3 w-3",
        "shrink-0 fill-amber-400 text-amber-500",
      )}
    />
  ));

  const ring = (
    <div
      className="relative shrink-0"
      style={{ width: sizePx, height: sizePx }}
    >
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle
          cx="18"
          cy="18"
          r={RADIUS}
          fill="none"
          className="stroke-slate-200"
          strokeWidth={ringStrokeWidth}
        />
        <circle
          cx="18"
          cy="18"
          r={RADIUS}
          fill="none"
          className={cn("transition-all duration-1000 ease-out", colorClass)}
          strokeWidth={ringStrokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${dashLength.toString()} ${CIRCUMFERENCE.toString()}`}
        />
      </svg>
      {layout === "column" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          {renderCenterText()}
        </div>
      ) : null}
    </div>
  );

  // Ring left, value/goal + label stacked to the right.
  if (layout === "row") {
    return (
      <Row className="shrink-0 items-center gap-4">
        {ring}
        <Column className="items-start gap-1">
          {hasGoal ? (
            <Row className="items-baseline gap-0.5">
              <Text className="text-xl font-semibold tabular-nums leading-none text-slate-900">
                {valueText}
              </Text>
              <Text className="text-base font-medium tabular-nums leading-none text-slate-400">
                /{goalText}
              </Text>
            </Row>
          ) : (
            <Text className="text-xl font-semibold tabular-nums leading-none text-slate-900">
              {valueText}
            </Text>
          )}
          <Row className="items-center gap-0.5">
            <Text className="text-xs font-medium uppercase leading-none tracking-wide text-slate-500">
              {label}
            </Text>
            {starIcons}
          </Row>
        </Column>
      </Row>
    );
  }

  const renderLabel = () => (
    <Row className="shrink-0 items-center justify-center gap-0.5">
      <Text className={cn(labelTextClass, labelClassName)}>{label}</Text>
      {starIcons}
    </Row>
  );

  const render = () => {
    // The label sits in its own bottom lane and the ring centers in what's
    // left, so sibling rings line up even when their labels wrap differently.
    if (isLabelBottomAligned) {
      return (
        <Column className="relative min-h-0 w-full flex-1 text-center">
          <Column className="absolute inset-x-0 bottom-5 top-0 items-center justify-center">
            {ring}
          </Column>
          <Row className="absolute inset-x-0 bottom-0 justify-center">
            {renderLabel()}
          </Row>
        </Column>
      );
    }

    return (
      <Column className="h-full min-h-0 w-full items-center justify-end text-center">
        <Column className={cn("items-center", isMini ? "gap-1" : "gap-2")}>
          {ring}
          {renderLabel()}
        </Column>
      </Column>
    );
  };

  return render();
};
